






































































































































































import axios from 'axios';
import moment from 'moment-timezone';
import Component from 'vue-class-component';

import { BaseVue } from '@/BaseVue';
import UiButton from '@/components/ui/UiButton.vue';
import UiDataTable from '@/components/ui/UiDataTable.vue';
import UiLoading from '@/components/ui/UiLoading.vue';
import UiPageHeader from '@/components/ui/UiPageHeader.vue';
import { MUT_SNACKBAR } from '@/store';
import { requestParentToNavigate } from '@/utils/iframeMessageRequester';

type PricingContextRecord = {
  id?: string;
  description?: string;
  resolution?: string;
  methodology?: string;
  action?: {
    type?: string;
  };
  isDisabled?: boolean;
  rules?: unknown[];
};

type PricingRouteRecord = {
  id?: string;
  routeToContextId?: string;
  walletId?: string;
  direction?: string;
  priority?: number;
  isDisabled?: boolean;
  metadata?: unknown;
  inclusiveStartTimestampSEC?: number;
  inclusiveEndTimestampSEC?: number;
};

type WalletRecord = {
  id?: string | null;
  name?: string | null;
};

@Component({
  components: {
    UiPageHeader,
    UiButton,
    UiDataTable,
    UiLoading,
  },
})
export default class PricingContext extends BaseVue {
  public baseUrl = process.env.VUE_APP_API_URL;

  public isLoadingContexts = false;
  public isLoadingRoutes = false;
  public pricingContexts: PricingContextRecord[] = [];
  public pricingRoutes: PricingRouteRecord[] = [];

  public contextHeaders = [
    {
      id: 'id',
      label: 'ID',
      defaultVisibility: true,
    },
    {
      id: 'description',
      label: 'Description',
      defaultVisibility: true,
    },
    {
      id: 'resolution',
      label: 'Resolution',
      defaultVisibility: true,
    },
    {
      id: 'methodology',
      label: 'Methodology',
      defaultVisibility: true,
    },
    {
      id: 'isDisabled',
      label: 'Status',
      defaultVisibility: true,
    },
    {
      id: 'actions',
      label: 'Actions',
      defaultVisibility: true,
    },
  ];

  public routeHeaders = [
    {
      id: 'id',
      label: 'ID',
      defaultVisibility: true,
    },
    {
      id: 'routeToContextId',
      label: 'Context ID',
      defaultVisibility: true,
    },
    {
      id: 'walletId',
      label: 'Wallet Name',
      defaultVisibility: true,
    },
    {
      id: 'direction',
      label: 'Direction',
      defaultVisibility: true,
    },
    {
      id: 'priority',
      label: 'Priority',
      defaultVisibility: true,
    },
    {
      id: 'isDisabled',
      label: 'Status',
      defaultVisibility: true,
    },
    {
      id: 'actions',
      label: 'Actions',
      defaultVisibility: true,
    },
  ];

  async mounted() {
    await Promise.all([this.loadPricingContexts(), this.loadPricingRoutes()]);
  }

  private async loadPricingContexts() {
    try {
      this.isLoadingContexts = true;
      const resp = await axios.get(`${this.baseUrl}/orgs/${this.$store.state.currentOrg.id}/pricing-contexts`, {
        withCredentials: true,
      });
      this.pricingContexts = (resp.data || []) as PricingContextRecord[];
    } catch (error) {
      console.error('Error loading pricing contexts:', error);
      this.$store.commit(MUT_SNACKBAR, {
        message: 'Error loading pricing contexts',
        color: 'error',
      });
    } finally {
      this.isLoadingContexts = false;
    }
  }

  private async loadPricingRoutes() {
    try {
      this.isLoadingRoutes = true;
      const resp = await axios.get(`${this.baseUrl}/orgs/${this.$store.state.currentOrg.id}/pricing-context-routes`, {
        withCredentials: true,
      });
      this.pricingRoutes = (resp.data || []) as PricingRouteRecord[];
    } catch (error) {
      console.error('Error loading pricing routes:', error);
      this.$store.commit(MUT_SNACKBAR, {
        message: 'Error loading pricing routes',
        color: 'error',
      });
    } finally {
      this.isLoadingRoutes = false;
    }
  }

  get wallets(): WalletRecord[] {
    return this.$store.state.wallets.wallets ?? [];
  }

  public getWalletName(walletId: string): string {
    const wallet = this.wallets.find((w) => w.id === walletId);
    return wallet ? wallet.name || walletId : walletId;
  }

  get hasPricingContextCsvRows(): boolean {
    return this.pricingContexts.length > 0 || this.pricingRoutes.length > 0;
  }

  public downloadPricingContextCsv() {
    if (!this.hasPricingContextCsvRows) {
      this.showDownloadError('There are no pricing contexts or routes to download.');
      return;
    }

    const header = [
      'Record Type',
      'ID',
      'Description',
      'Resolution',
      'Methodology',
      'Action Type',
      'Rule Count',
      'Context ID',
      'Wallet ID',
      'Wallet Name',
      'Direction',
      'Priority',
      'Status',
      'Metadata',
      'Start Date',
      'End Date',
    ];

    const routesByContextId = new Map<string, PricingRouteRecord[]>();
    for (const route of this.pricingRoutes) {
      const contextId = route.routeToContextId ?? '';
      const contextRoutes = routesByContextId.get(contextId) ?? [];
      contextRoutes.push(route);
      routesByContextId.set(contextId, contextRoutes);
    }

    const rows: unknown[][] = [];
    for (const context of this.pricingContexts) {
      rows.push(this.buildPricingContextCsvRow(context));

      const contextId = context.id ?? '';
      const contextRoutes = contextId ? routesByContextId.get(contextId) ?? [] : [];
      for (const route of contextRoutes) {
        rows.push(this.buildPricingRouteCsvRow(route));
      }
      if (contextId) routesByContextId.delete(contextId);
    }

    for (const orphanRoutes of routesByContextId.values()) {
      for (const route of orphanRoutes) {
        rows.push(this.buildPricingRouteCsvRow(route));
      }
    }

    this.downloadCsv('pricing-context', [header, ...rows]);
  }

  private buildPricingContextCsvRow(context: PricingContextRecord): unknown[] {
    return [
      'Pricing Context',
      context.id,
      context.description,
      context.resolution,
      context.methodology,
      context.action?.type ?? '',
      Array.isArray(context.rules) ? context.rules.length : 0,
      '',
      '',
      '',
      '',
      '',
      context.isDisabled ? 'Disabled' : 'Active',
      '',
      '',
      '',
    ];
  }

  private buildPricingRouteCsvRow(route: PricingRouteRecord): unknown[] {
    return [
      'Pricing Route',
      route.id,
      '',
      '',
      '',
      '',
      '',
      route.routeToContextId,
      route.walletId,
      route.walletId ? this.getWalletName(route.walletId) : '',
      route.direction,
      route.priority,
      route.isDisabled ? 'Disabled' : 'Active',
      this.formatJson(route.metadata),
      this.formatTimestampSEC(route.inclusiveStartTimestampSEC),
      this.formatTimestampSEC(route.inclusiveEndTimestampSEC),
    ];
  }

  private formatTimestampSEC(value: unknown): string {
    const sec = Number(value);
    if (!Number.isFinite(sec) || sec === Number.MAX_SAFE_INTEGER) return '';
    return moment.unix(sec).tz(this.$store.state.currentOrg.timezone).format('YYYY-MM-DD');
  }

  private formatJson(value: unknown): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  private downloadCsv(fileNamePrefix: string, rows: unknown[][]): void {
    const csv = rows.map((row) => row.map((cell) => this.csvEscape(cell)).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileNamePrefix}-${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  private csvEscape(value: unknown): string {
    const text = value == null ? '' : String(value);
    if (/[",\n\r]/.test(text)) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  }

  private showDownloadError(message: string): void {
    this.$store.commit(MUT_SNACKBAR, {
      message,
      color: 'error',
    });
  }

  public createPricingContext() {
    requestParentToNavigate('/pricing/context/new');
  }

  public editPricingContext(id: string) {
    requestParentToNavigate(`/pricing/context/${id}`);
  }

  public createPricingRoute() {
    requestParentToNavigate('/pricing/context/route/new');
  }

  public editPricingRoute(id: string) {
    requestParentToNavigate(`/pricing/context/route/${id}`);
  }
}
